import { EnvironmentInjector, inject, Injectable, runInInjectionContext } from '@angular/core';
import {
  type HttpEvent,
  type HttpHandler,
  type HttpInterceptor,
  type HttpRequest,
} from '@angular/common/http';
import { from, type Observable, switchMap } from 'rxjs';
import { isEndToEndTesting, TRUEFORM_UI_CONFIG, type TrueformUiConfig } from '@trueform/ui/core';

interface InterceptorCtor {
  new (): HttpInterceptor;
}

/**
 * Eager, tiny delegator for an e2e-only interceptor whose heavy code lives in a
 * lazy chunk ([[TASK-049]] T1).
 *
 * The library `E2eMockInterceptor` is registered unconditionally in
 * `app.config.ts` but only ever active under `?e2e=true`; importing it
 * statically dragged `@trueform/ui/testing` (15.9 kB) into the initial bundle.
 * This delegator keeps the interceptor at its exact position in the
 * `HTTP_INTERCEPTORS` chain while the real class is pulled in by a dynamic
 * `import()` on the first intercepted request: `intercept()` receives `next`
 * and hands it straight to the real interceptor, so the lazy code runs exactly
 * where the eager code used to.
 *
 * The app-owned DCS half (`AppE2eMockDelegatorInterceptor`) was retired by
 * [[TASK-071]]: those routes are now MSW handlers (`src/testing/msw/dcs/`)
 * registered by the `main.ts` e2e bootstrap. The UAS surface moved into the
 * library's own contract-faithful mock (WUIAM-140 / REQ-020), so only the
 * library half remains here.
 *
 * Requests while the flag is off short-circuit to `next.handle(req)` and never
 * import the heavy chunk.
 */
abstract class LazyE2eMockInterceptor implements HttpInterceptor {
  private readonly injector = inject(EnvironmentInjector);
  private readonly uiConfig: TrueformUiConfig = inject(TRUEFORM_UI_CONFIG);

  private loading: Promise<HttpInterceptor> | null = null;
  private warned = false;

  /** Dynamically imports the heavy interceptor class from its lazy chunk. */
  protected abstract loadReal(): Promise<InterceptorCtor>;

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Same activation signal as the eager code it replaces (evaluated per
    // request so the ?e2e / localStorage flag can flip during a session).
    if (!isEndToEndTesting(this.uiConfig.production)) {
      return next.handle(req);
    }
    return from(this.realInterceptor()).pipe(
      // Delegating with the SAME `next` preserves the chain position.
      switchMap((real) => real.intercept(req, next)),
    );
  }

  private realInterceptor(): Promise<HttpInterceptor> {
    if (!this.loading) {
      this.loading = this.loadReal()
        .then((ctor) => runInInjectionContext(this.injector, () => new ctor()))
        .catch((err: unknown) => {
          // Do not poison the cache: reset so a later request retries the
          // import instead of inheriting this rejection for the session.
          this.loading = null;
          if (!this.warned) {
            this.warned = true;
            console.warn('E2E mock delegator: failed to load its lazy e2e mock interceptor.', err);
          }
          throw err;
        });
    }
    return this.loading;
  }
}

/**
 * Delegates to the library's `E2eMockInterceptor` (`@trueform/ui/testing`),
 * which serves the users/roles/permissions mocks. Must stay FIRST in the chain.
 */
@Injectable()
export class LibraryE2eMockDelegatorInterceptor extends LazyE2eMockInterceptor {
  protected override loadReal(): Promise<InterceptorCtor> {
    return import('@trueform/ui/testing').then((m) => m.E2eMockInterceptor);
  }
}
